import React, { useState } from "react";
import {
    Box,
    Button,
    Typography,
    RadioGroup,
    FormControlLabel,
    Radio,
    Container,
    Dialog,
    DialogContent,
    IconButton,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import LoginQRModal from "../../components/LoginQRModal ";
import { getQuestions, submitQuiz } from "../../services/QuestionService";
import CloseIcon from "@mui/icons-material/Close";
import { CustomButton } from "../../utils/CustomTagComponents";
import { showSuccessToast } from "../../components/common/Toast";

const LIMIT = 10;

const QuizComponent = () => {
    const { auth } = useSelector((state) => state.auth || {});
    const isLoggedIn = Boolean(auth?.idToken || auth?.localId || auth?.email);

    const [showLoginModel, setShowLoginModel] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [openQuestionnaire, setOpenQuestionnaire] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [answers, setAnswers] = useState({});

    /* ================= FETCH QUESTIONS WITH CACHE ================= */
    const { data, isLoading } = useQuery({
        queryKey: ["questions", currentPage],
        queryFn: async () => {
            const res = await getQuestions(currentPage, LIMIT);
            if (res.data.success) {
                return res.data;
            }
            return null;
        },
    });

    const questions = data?.data || [];
    const totalPages = data?.pagination?.totalPages || 1;

    /* ================= HANDLE ANSWERS ================= */
    const handleOptionChange = (questionId, optionId) => {
        setAnswers((prev) => ({
            ...prev,
            [questionId]: optionId,
        }));
    };

    /* ================= SUBMIT ================= */
    const handleSubmit = async () => {
        if (!isLoggedIn) {
            setShowLoginModel(true);
            return;
        }

        const payload = {
            user_id: 1,
            answers: Object.entries(answers).map(
                ([question_id, selected_option_id]) => ({
                    question_id: Number(question_id),
                    selected_option_id,
                })
            ),
        };

        try {
            await submitQuiz(payload);
            setOpenQuestionnaire(false);
            showSuccessToast("Quiz submitted successfully!");
        } catch (err) {
            console.error("Submit failed", err);
        }
    };

    /* ================= CURRENT QUESTION ================= */
    const currentQuestion = questions[currentIndex];
    const globalQuestionNumber = (currentPage - 1) * LIMIT + currentIndex + 1;

    /* ================= NAVIGATION ================= */
    const handleNext = () => {
        if (currentIndex < questions.length - 1) {
            setCurrentIndex((prev) => prev + 1);
        } else if (currentPage < totalPages) {
            setCurrentPage((prev) => prev + 1);
            setCurrentIndex(0); // Reset index on new page
        }
    };

    const handlePrevious = () => {
        if (currentIndex > 0) {
            setCurrentIndex((prev) => prev - 1);
        } else if (currentPage > 1) {
            setCurrentPage((prev) => prev - 1);
            setCurrentIndex(LIMIT - 1); // Go to last index of previous page
        }
    };

    /* ================= UI ================= */
    return (
        <>
            <Container maxWidth="md" sx={{
                overflow: 'hidden',
                py: { xs: 2, sm: 2, md: 3 },
                px: { xs: 3, sm: 4, md: 6 },
                paddingLeft: { xs: "24px !important", md: "48px !important" },
                paddingRight: { xs: "24px !important", md: "48px !important" }
            }}>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Box
                        sx={{
                            maxWidth: 900,
                            width: "100%",
                            p: 3,
                            borderRadius: "16px",
                            background: "linear-gradient(135deg, #fff5f5, #ffecec)",
                            border: "1px solid #ffd6d6",
                            textAlign: "center",
                            boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                            transition: "all 0.3s ease",
                            "&:hover": {
                                transform: "translateY(-4px)",
                                boxShadow: "0 12px 30px rgba(0,0,0,0.1)",
                            },
                        }}
                    >
                        <Typography sx={{ fontSize: { xs: "22px", sm: "26px", md: "30px" }, fontWeight: 700, color: "#111", mb: 1 }}>
                            🎯 Find Your Perfect Program
                        </Typography>
                        <Typography sx={{ fontSize: { xs: "14px", sm: "16px" }, color: "black", mb: { xs: 1, sm: 2 } }}>
                            Answer a few quick questions and get personalized course recommendations tailored to your goals.
                        </Typography>
                        <CustomButton variant="contained" onClick={() => setOpenQuestionnaire(true)}>
                            Start Questionnaire
                        </CustomButton>
                    </Box>
                </Box>

                <Dialog open={openQuestionnaire} onClose={() => setOpenQuestionnaire(false)} fullWidth>
                    <DialogContent sx={{ p: 0 }}>
                        <IconButton sx={{ position: "absolute", top: 10, right: 10, color: "#555" }} onClick={() => setOpenQuestionnaire(false)}>
                            <CloseIcon />
                        </IconButton>
                        <Box sx={{ p: 3, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ fontFamily: '"Nunito Sans", sans-serif', fontWeight: 800, fontSize: "22px", color: "#111", mb: 2 }}>
                                Questionnaire
                            </Typography>

                            {isLoading ? (
                                <Box textAlign="center" mt={4}>
                                    <Typography color="text.secondary">Loading questions...</Typography>
                                </Box>
                            ) : questions.length === 0 ? (
                                <Box textAlign="center" mt={4}>
                                    <Typography variant="h6" color="text.secondary">No questions available</Typography>
                                </Box>
                            ) : (
                                currentQuestion && (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" mb={1}>
                                            Question {globalQuestionNumber}
                                        </Typography>
                                        <Typography variant="subtitle1" mb={1}>
                                            {globalQuestionNumber}. {currentQuestion.question}
                                        </Typography>
                                        {currentQuestion.description && (
                                            <Typography variant="body2" color="text.secondary" mb={2}>
                                                {currentQuestion.description}
                                            </Typography>
                                        )}
                                        <RadioGroup
                                            value={answers[currentQuestion.id] || ""}
                                            onChange={(e) => handleOptionChange(currentQuestion.id, Number(e.target.value))}
                                        >
                                            {currentQuestion.options.map((opt) => (
                                                <FormControlLabel key={opt.id} value={opt.id} control={<Radio />} label={opt.option_text} />
                                            ))}
                                        </RadioGroup>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            sx={{ mt: 1, color: 'black', borderColor: 'black' }}
                                            onClick={() => setAnswers((prev) => {
                                                const copy = { ...prev };
                                                delete copy[currentQuestion.id];
                                                return copy;
                                            })}
                                        >
                                            Clear Answer
                                        </Button>
                                    </Box>
                                )
                            )}

                            {questions.length > 0 && (
                                <Box mt={4} display="flex" justifyContent="space-between" gap={2}>
                                    <Button
                                        variant="contained"
                                        disabled={currentPage === 1 && currentIndex === 0}
                                        onClick={handlePrevious}
                                        sx={{ backgroundColor: "#fd0000", color: "#fff", "&:hover": { backgroundColor: "#e60000" } }}
                                    >
                                        Previous
                                    </Button>
                                    {currentPage === totalPages && currentIndex === questions.length - 1 ? (
                                        <Button
                                            variant="contained"
                                            onClick={handleSubmit}
                                            sx={{ backgroundColor: "#fd0000", color: "#fff", "&:hover": { backgroundColor: "#e60000" } }}
                                        >
                                            Submit
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="contained"
                                            onClick={handleNext}
                                            sx={{ backgroundColor: "#fd0000", color: "#fff", "&:hover": { backgroundColor: "#e60000" } }}
                                        >
                                            Next
                                        </Button>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </DialogContent>
                </Dialog>
            </Container>

            {showLoginModel && <LoginQRModal open={showLoginModel} handleClose={() => setShowLoginModel(false)} />}
        </>
    );
};

export default QuizComponent;
